import useDate from 'src/CrudManager/Core/composables/useDate'
import { usePunchStore } from 'src/stores/usePunchStore'
import type { Ref } from 'vue'
import { onUnmounted, ref, watch } from 'vue'

export interface PunchTimers {
  breakDuration: Ref<string>
  currentTime: Ref<Date>
  setupTimers: (breakStartTime: Ref<string>, punchInTime: Ref<string>, punchOutTime: Ref<string>, onTimeUpdate?: () => void) => void
  cleanupTimers: () => void
}

// Create a singleton instance
let instance: PunchTimers | null = null

export function usePunchTimers(): PunchTimers {
  if (instance) {
    return instance
  }

  const punchStore = usePunchStore()
  const { formatTimeSpent } = useDate()

  const breakDurationTimer = ref<number | null>(null)
  const workTimeTimer = ref<number | null>(null)
  const breakDuration = ref('')
  const currentTime = ref(new Date())

  // Calculate break duration
  const updateBreakDuration = (breakStartTime: string, onTimeUpdate?: () => void) => {
    if (punchStore.isOnBreak && breakStartTime) {
      currentTime.value = new Date() // Update current time to trigger reactivity
      breakDuration.value = formatTimeSpent(breakStartTime, currentTime.value.toISOString())
      onTimeUpdate?.()

      // Schedule next update in 1 minute
      if (breakDurationTimer.value) {
        clearTimeout(breakDurationTimer.value)
      }
      breakDurationTimer.value = window.setTimeout(() => updateBreakDuration(breakStartTime, onTimeUpdate), 60000)
    }
  }

  // Update working time every minute
  const updateWorkTime = (punchInTime: string, punchOutTime: string, onTimeUpdate?: () => void) => {
    if (punchInTime && !punchOutTime) {
      // Update current time to trigger reactivity
      currentTime.value = new Date()
      onTimeUpdate?.()

      // Trigger a refresh every minute to update the UI
      workTimeTimer.value = window.setTimeout(() => updateWorkTime(punchInTime, punchOutTime, onTimeUpdate), 60000)
    }
  }

  const setupTimers = (breakStartTime: Ref<string>, punchInTime: Ref<string>, punchOutTime: Ref<string>, onTimeUpdate?: () => void) => {
    // Watch for changes in break status and break start time
    watch(
      [() => punchStore.isOnBreak, () => breakStartTime.value],
      ([isOnBreak, startTime]) => {
        // Clear any existing timer
        if (breakDurationTimer.value) {
          clearTimeout(breakDurationTimer.value)
          breakDurationTimer.value = null
        }

        if (isOnBreak && startTime) {
          // Start tracking break duration with the new start time
          updateBreakDuration(startTime, onTimeUpdate)
        } else {
          breakDuration.value = ''
        }
      },
      { immediate: true },
    )

    // Watch punch in status to start work time tracking
    watch(
      () => punchInTime.value,
      (newVal) => {
        if (newVal && !punchOutTime.value) {
          updateWorkTime(newVal, punchOutTime.value, onTimeUpdate)
        }
      },
      { immediate: true },
    )
  }

  const cleanupTimers = () => {
    if (breakDurationTimer.value) {
      clearTimeout(breakDurationTimer.value)
      breakDurationTimer.value = null
    }
    if (workTimeTimer.value) {
      clearTimeout(workTimeTimer.value)
      workTimeTimer.value = null
    }
    // Reset the instance when cleaning up
    instance = null
  }

  // Clean up timers when component unmounts
  onUnmounted(cleanupTimers)

  instance = {
    breakDuration,
    currentTime,
    setupTimers,
    cleanupTimers,
  }

  return instance
}
